/**
 * NotificationSettings — Benachrichtigungs-Präferenzen (WP7).
 *
 * E-Mail-/Push-/SMS-Toggles, angebunden an das Benachrichtigungssystem:
 *   GET /v2/admin/notification-preferences  → initialer Zustand
 *   PUT /v2/admin/notification-preferences  → Persistierung je Toggle
 *
 * UX folgt der Darstellung-Sektion: Änderungen werden sofort (optimistisch)
 * übernommen und persistiert; schlägt das PUT fehl, wird der Toggle
 * zurückgerollt und eine Fehlermeldung angezeigt. Der frühere
 * "Täglicher Überblick"-Eintrag (digest) ist entfernt — er hatte kein
 * Backend-Pendant.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Toggle, Button } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { notificationPreferencesApi, type NotificationPreferences } from '../../lib/api';
import {
  browserNotificationPermission, requestBrowserNotificationPermission, showBrowserNotification,
} from '../../lib/notifications';

interface NotificationSettingsProps {
  dark: boolean;
  /** Karten-Innenabstand — von der Einstellungen-Seite (Dichte) durchgereicht. */
  cardPad?: number;
}

export function NotificationSettings({ dark, cardPad = 20 }: NotificationSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  // Browser-Notification-Berechtigung (Web Notifications API).
  const [browserPerm, setBrowserPerm] = useState(browserNotificationPermission());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const p = await notificationPreferencesApi.get();
      setPrefs(p);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optimistisch togglen + persistieren; bei Fehler Rollback auf den
  // vorherigen Zustand.
  const toggle = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!prefs) return;
    const previous = prefs;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaveError(false);
    try {
      const saved = await notificationPreferencesApi.update(next);
      setPrefs(saved);
    } catch {
      setPrefs(previous);
      setSaveError(true);
    }
  };

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: cardPad }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('common.loading')}</div>
      </Card>
    );
  }

  if (loadError || !prefs) {
    return (
      <Card dark={dark} style={{ padding: cardPad }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>
          {tr('settings.notifications.load_error')}
        </div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void load()}>
          {tr('settings.notifications.retry')}
        </Button>
      </Card>
    );
  }

  const rows: { k: keyof NotificationPreferences; l: string; d: string }[] = [
    { k: 'email', l: tr('settings.notifications.email'), d: tr('settings.notifications.email_desc') },
    { k: 'push',  l: tr('settings.notifications.push'),  d: tr('settings.notifications.push_desc') },
    { k: 'sms',   l: tr('settings.notifications.sms'),   d: tr('settings.notifications.sms_desc') },
  ];

  return (
    <Card dark={dark} style={{ padding: cardPad }}>
      <SectionTitle dark={dark} title={tr('settings.notifications.title')} subtitle={tr('settings.notifications.subtitle')} />
      {rows.map((n, i) => (
        <div
          key={n.k}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0', borderBottom: i < rows.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>{n.l}</div>
            <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>{n.d}</div>
          </div>
          <Toggle
            checked={prefs[n.k]}
            onChange={v => void toggle(n.k, v)}
          />
        </div>
      ))}
      {saveError && (
        <div style={{ fontSize: 12, color: '#b42318', marginTop: 10 }}>
          {tr('settings.notifications.save_error')}
        </div>
      )}

      {/* Browser-Benachrichtigungen (Web Notifications API) — Berechtigung
          anfragen + Testbenachrichtigung. Neue CRM-Benachrichtigungen werden
          danach automatisch als Browser-Notification angezeigt. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${t.borderSoft}`, marginTop: 4, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: t.heading }}>{tr('settings.notifications.browser')}</div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
            {browserPerm === 'granted'
              ? tr('settings.notifications.browser_granted')
              : browserPerm === 'denied'
                ? tr('settings.notifications.browser_denied')
                : browserPerm === 'unsupported'
                  ? tr('settings.notifications.browser_unsupported')
                  : tr('settings.notifications.browser_desc')}
          </div>
        </div>
        {browserPerm === 'granted' ? (
          <Button
            variant="secondary" size="sm" dark={dark}
            onClick={() => showBrowserNotification(tr('settings.notifications.browser_test_title'), tr('settings.notifications.browser_test_body'))}
          >
            {tr('settings.notifications.browser_test')}
          </Button>
        ) : (
          <Button
            variant="primary" size="sm" dark={dark}
            disabled={browserPerm === 'denied' || browserPerm === 'unsupported'}
            onClick={async () => {
              const p = await requestBrowserNotificationPermission();
              setBrowserPerm(p);
              if (p === 'granted') {
                showBrowserNotification(tr('settings.notifications.browser_test_title'), tr('settings.notifications.browser_test_body'));
              }
            }}
          >
            {tr('settings.notifications.browser_enable')}
          </Button>
        )}
      </div>
    </Card>
  );
}
